import { Button, List, ListItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LatLng } from 'shared/types';
import { useMapState } from 'state/useMapState';
import { useAuthState } from 'state/useAuthState';
import { Link } from 'react-router-dom';
import { CoordinateSelector } from 'components/CoordinateSelector';
import { CoordinatesListItem } from 'components/CoordinatesListItem';
import { SlackChannelDeepLink } from 'components/SlackChannelDeepLink';
import { MAP_ENTITY_DRAWER_MAX_SNAP_PCT } from 'features/mapping/components/MapEntityDrawer/MapEntityDrawer.constants';
import { useMapEntityDrawer } from 'features/mapping/hooks/useMapEntityDrawer';
import { Place } from 'features/places/places.types';
import { useCurrentLocation, buildLocationState } from 'hooks/useLocationState';
import { useReleaseFlag } from 'hooks/useReleaseFlags';

type MapPinInfoProps = {
  coordinates: LatLng;
  place?: Place;
  showSlackLinks?: boolean;
  wildfireChannel?: string;
  wildfireChannelId?: string | null;
  floodingChannel?: string;
  floodingChannelId?: string | null;
  showFireInfo?: boolean;
  showFloodingInfo?: boolean;
};

export const MapPinInfo = (props: MapPinInfoProps): JSX.Element => {
  const {
    coordinates,
    place,
    showSlackLinks = true,
    wildfireChannel,
    wildfireChannelId,
    floodingChannel,
    floodingChannelId,
    showFireInfo = true,
    showFloodingInfo = true,
  } = props;
  const { t } = useTranslation();
  const {
    permissions: {
      isInternalUser,
      canReportWildfire,
      canReportFlooding,
      isRxOnlyReporter,
    },
  } = useAuthState();
  const { incidentMapState } = useMapState();
  const { snapEntityDrawerTo } = useMapEntityDrawer();

  const editPlaceState = buildLocationState(useCurrentLocation());

  const pinImprovements = useReleaseFlag('pin_drop_improvements');

  const createRoutePathParams = new URLSearchParams({
    lat: coordinates.lat.toString(),
    lng: coordinates.lng.toString(),
    zoom: Math.floor(incidentMapState.zoom).toString() || '',
  });

  return (
    <List disablePadding>
      {pinImprovements ? (
        <ListItem disablePadding divider sx={{ display: 'block', py: 1 }}>
          <CoordinateSelector
            coordinates={coordinates}
            // Grow the mobile sheet so the format list isn't cut off.
            onExpand={() => snapEntityDrawerTo(MAP_ENTITY_DRAWER_MAX_SNAP_PCT)}
          />
        </ListItem>
      ) : (
        <>
          <CoordinatesListItem coordinates={coordinates} format="decimal" />
          <CoordinatesListItem coordinates={coordinates} format="DDM" />
          <CoordinatesListItem coordinates={coordinates} format="DMS" />
        </>
      )}

      {!pinImprovements && isInternalUser && showSlackLinks && (
        <>
          {showFireInfo && (
            <SlackChannelDeepLink
              label={t('externalGeoEvent.slackChannel.wildfire')}
              channelId={wildfireChannelId}
              channelName={wildfireChannel}
            />
          )}
          {showFloodingInfo && (
            <SlackChannelDeepLink
              label={t('externalGeoEvent.slackChannel.flooding')}
              channelId={floodingChannelId}
              channelName={floodingChannel}
            />
          )}
        </>
      )}

      {pinImprovements && place && (
        <ListItem sx={{ pt: 2, pb: 1 }}>
          <Button
            size="large"
            component={Link}
            to={{
              pathname: `/place/${place.id}/edit`,
              state: editPlaceState,
            }}
            fullWidth
          >
            {t('mapPin.editPlace')}
          </Button>
        </ListItem>
      )}

      {canReportWildfire && !place && (
        <>
          {!isRxOnlyReporter && showFireInfo && (
            <ListItem sx={{ pt: 2, pb: 1 }}>
              <Button
                size="large"
                fullWidth
                component={Link}
                to={`/incident/create?${createRoutePathParams.toString()}`}
              >
                {t('mapPin.info.createWildfire')}
              </Button>
            </ListItem>
          )}
          {showFireInfo && (
            <ListItem sx={{ pt: isRxOnlyReporter ? 2 : 1, pb: 1 }}>
              <Button
                size="large"
                fullWidth
                component={Link}
                to={`/incident/create?${createRoutePathParams.toString()}&prescribed=true`}
              >
                {t('mapPin.info.createPrescribedBurn')}
              </Button>
            </ListItem>
          )}
        </>
      )}

      {canReportFlooding && showFloodingInfo && !place && (
        <ListItem sx={{ py: 1 }}>
          <Button
            size="large"
            fullWidth
            component={Link}
            to={`/flooding/create?${createRoutePathParams.toString()}`}
          >
            {t('mapPin.info.createFlooding')}
          </Button>
        </ListItem>
      )}
    </List>
  );
};
